import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { DmDataService } from '../dm-data/dm-data.service';
import { lazyInject } from '@app/shared/decorators';
import { CurrencyPipe } from '@angular/common';
import { get, isFunction, padEnd } from 'lodash';
import { STATE_KEYS } from '../state/state-selectors';
import {
  DEFAULT_CURRENCY_SYMBOL_POSITION,
  DEFAULT_DECIMAL_DIVIDER,
  DEFAULT_NEGATIVES,
  DEFAULT_NUMBER_PRECISION,
  DEFAULT_THOUSANDS_DIVIDER,
  IFormattingType,
  NUMERICAL_ORDER_AMOUNT,
} from '../number-format/number-format.constants';

/*
  CountryFormatService
  should includes all methods to format and display country-specific values
*/
@Injectable()
export class CountryFormatService {
  @lazyInject(DmDataService) public dmDataService: DmDataService;

  public symbolPosition: string = DEFAULT_CURRENCY_SYMBOL_POSITION;
  public numberPrecision: number = DEFAULT_NUMBER_PRECISION;

  public constructor(
    public currencyPipe: CurrencyPipe,
    public injector: Injector,
  ) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public applyNumberFormat(
    input: number,
    formattingOption: IFormattingType,
  ): string {
    const precision: number = get(
      formattingOption,
      'precision',
      this.getPrecision(),
    );

    const decimalSeparator = !precision
      ? ''
      : get(formattingOption, 'decimalSeparator', this.getDecimalSeparator());

    const thousandsSeparator: string = get(
      formattingOption,
      'thousandsSeparator',
      this.getThousandsSeparator(),
    );

    const sign = input < 0 ? '-' : '';

    const basePart = this.formNumberBasePart(input, thousandsSeparator);
    const mantissa = padEnd(String(input).split('.')[1], precision, '0').slice(
      0,
      precision,
    );

    return sign + basePart + decimalSeparator + mantissa;
  }

  public getPrecision(): any {
    return this.getPreferencesProperty(
      STATE_KEYS.XDM_PRECISION,
      DEFAULT_NUMBER_PRECISION,
    );
  }

  public getThousandsSeparator(): any {
    return this.getPreferencesProperty(
      STATE_KEYS.XDM_THOUSANDS_SEPARATOR,
      DEFAULT_THOUSANDS_DIVIDER,
    );
  }

  public getDecimalSeparator(): any {
    return this.getPreferencesProperty(
      STATE_KEYS.XDM_DECIMAL_SEPARATOR,
      DEFAULT_DECIMAL_DIVIDER,
    );
  }

  public getNegatives(): any {
    return this.getPreferencesProperty(
      STATE_KEYS.XDM_NEGATIVES,
      DEFAULT_NEGATIVES,
    );
  }

  public getQuantityPrecision(): any {
    return Number(
      this.getPreferencesProperty(STATE_KEYS.QUANTITY_PRECISION, 2),
    );
  }

  private getPreferencesProperty(
    path?: string | string[],
    defaultValue?: any,
  ): any {
    return get(this.dmDataService.companyPreferences, path, defaultValue);
  }

  /**
   * Returns an input number divided into groups by thousandSeparator as a string
   */
  private formNumberBasePart(
    input: number,
    thousandsSeparator: string,
  ): string {
    const segmentSize = NUMERICAL_ORDER_AMOUNT;
    const baseNumberString = String(Math.trunc(Math.abs(input)));

    const regex = RegExp(
      `\\d{1,${segmentSize}}(?=(\\d{${segmentSize}})+(?!\\d))`,
      'g',
    );

    return baseNumberString.replace(regex, `$&${thousandsSeparator}`);
  }
}
